import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  Box, Button, IconButton, Stack, Tooltip, Typography,
  type SxProps, type Theme,
} from '@mui/material';
import { useState, type ReactNode } from 'react';

/** Scoped to the staff customer workspace; does not change customer-web styles. */
export const customerWorkspaceSx: SxProps<Theme> = {
  minWidth: 0,
  '& .MuiButton-root': { minHeight: 40, px: 2, py: 1, fontSize: 14 },
  '& .MuiButton-sizeSmall': { minHeight: { xs: 44, sm: 36 }, px: 1.5, py: 0.75 },
  '& .workspace-link': { border: 0, color: 'primary.main', px: 0.5, minWidth: 0 },
  '& .MuiTableCell-root': { fontSize: 14, py: 2, borderColor: 'divider' },
  '& .MuiTableCell-head': { py: 1.5, fontSize: 11, color: 'text.secondary', bgcolor: 'rgba(15,30,54,0.55)' },
  '& .MuiTypography-subtitle2': { fontSize: 16, fontWeight: 600 },
};

export function WorkspaceSectionHeader({ title, description, actions }: {
  title: string; description?: string; actions?: ReactNode;
}): JSX.Element {
  return <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2.5 }}>
    <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
      <Typography component="h2" variant="h6">{title}</Typography>
      {description ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography> : null}
    </Box>
    {actions ? <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{actions}</Stack> : null}
  </Stack>;
}

export function DetailField({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="dt">{label}</Typography>
    <Box component="dd" sx={{ m: 0, mt: 0.75, overflowWrap: 'anywhere', fontSize: 14 }}>{children}</Box>
  </Box>;
}

export function IdentityField({ label, children, action }: { label: string; children: ReactNode; action?: ReactNode }): JSX.Element {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,90px) minmax(0,1fr)', sm: '140px minmax(0,1fr) auto' }, gap: 1.5, alignItems: 'center', py: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
    <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
    <Box component="dd" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere', fontSize: 14 }}>{children}</Box>
    {action ? <Box sx={{ gridColumn: { xs: 2, sm: 3 }, justifySelf: 'start' }}>{action}</Box> : null}
  </Box>;
}

export function CopyValue({ value, label }: { value: string; label: string }): JSX.Element {
  const [feedback, setFeedback] = useState('');
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback('Copied');
    } catch {
      setFeedback('Select the value above to copy it.');
    }
  };
  return <Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
    <Typography component="code" variant="body2" sx={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere', userSelect: 'all' }}>{value}</Typography>
    <Button size="small" className="workspace-link" startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
      onClick={() => void copy()}>
      Copy {label}
    </Button>
    <Typography variant="caption" role="status" color="text.secondary">{feedback}</Typography>
  </Stack>;
}

/** The full customer ID under the name, with a one-click copy. */
export function CustomerIdentifier({ customerId }: { customerId: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(customerId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard refused (permissions, insecure origin): the ID is selectable.
    }
  };
  return <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">ID</Typography>
    <Typography component="code" variant="caption" data-testid="customer-id"
      sx={{ fontFamily: 'var(--font-mono)', color: 'text.primary', overflowWrap: 'anywhere', userSelect: 'all' }}>
      {customerId}
    </Typography>
    <Tooltip title={copied ? 'Copied' : 'Copy customer ID'}>
      <IconButton size="small" aria-label="Copy customer ID" onClick={() => void copy()} sx={{ p: 0.5 }}>
        {copied ? <CheckIcon fontSize="inherit" color="success" /> : <ContentCopyOutlinedIcon fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  </Stack>;
}
